import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  useTheme,
  Typography
} from '@mui/material';

function EnergiesTableContentPDF({
  singleReport,
  reportType,
  month,
  subReportGroupSelection,
  heatmapOn
}) {

  console.log("singleReport in EnergiesTableContentPDF: ", singleReport);

  const theme = useTheme(); // This will now be the light theme from ThemeProvider
  const getMeasurements = () => {
    if (!singleReport || !singleReport.SubReports) return [];

    if (subReportGroupSelection && singleReport.SubReports[subReportGroupSelection]) {
      return singleReport.SubReports[subReportGroupSelection];
    } else {
      return Object.values(singleReport.SubReports).flat();
    }
  };

  const measurements = getMeasurements();

  // Calculate heatmap percentages for all measurements
  const calculateHeatmapPercentages = (measurements) => {
    // Collect all values to find min/max for percentage calculation
    const allValues = [];
    measurements.forEach(measurement => {
      Object.values(measurement.Values).forEach(value => {
        if (typeof value === 'number' && !isNaN(value)) {
          allValues.push(value);
        }
      });
    });

    if (allValues.length === 0) return measurements;

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const range = maxValue - minValue;

    // Add percentage to each measurement's values
    return measurements.map(measurement => ({
      ...measurement,
      Values: Object.entries(measurement.Values).map(([key, value]) => ({
        date: key,
        value: typeof value === 'number' ? value : 0,
        percentage: range === 0 ? 0 : (value - minValue) / range
      }))
    }));
  };

  const measurementsWithHeatmap = calculateHeatmapPercentages(measurements);

  // Get unique keys for all the 'dates and times' from the measurements
  const keys = Array.from(new Set(
    measurements.flatMap(measurement => Object.keys(measurement.Values))
  )).sort();

  // Split keys into chunks for PDF-friendly tables with custom logic
  const createCustomChunks = (array) => {
    if (reportType === "Day") {
      // Split hours: 0-12 and 13-24 (2 tables of 12 hours each)
      const chunks = [];
      const midPoint = 12;

      // Find the index where hours switch from AM to PM
      const sortedArray = [...array].sort((a, b) => parseInt(a) - parseInt(b));
      const splitIndex = sortedArray.findIndex(key => parseInt(key) >= midPoint);

      if (splitIndex > 0) {
        chunks.push(sortedArray.slice(0, splitIndex)); // 0-11 hours
        chunks.push(sortedArray.slice(splitIndex));     // 12-23 hours
      } else {
        chunks.push(sortedArray); // Fallback if split logic fails
      }

      return chunks;
    } else {
      // Split days: 10 + 10 + remaining (3 tables)
      const chunks = [];
      const sortedArray = [...array].sort((a, b) => parseInt(a) - parseInt(b));

      if (sortedArray.length > 20) {
        chunks.push(sortedArray.slice(0, 10));   // Days 1-10
        chunks.push(sortedArray.slice(10, 20));  // Days 11-20
        chunks.push(sortedArray.slice(20));      // Days 21-end
      } else if (sortedArray.length > 10) {
        chunks.push(sortedArray.slice(0, 10));   // Days 1-10
        chunks.push(sortedArray.slice(10));      // Days 11-end
      } else {
        chunks.push(sortedArray); // Single table if ≤10 days
      }

      return chunks;
    }
  };

  const keyChunks = createCustomChunks(keys);

  // Format dates for display
  const formatDate = (key) => {
    // key is just a 01, 02, 03, 04 etc... If reportType === "Day" it means time of day, if reportType === "Month" it means the day of month
    if (reportType === "Day") {
      return `${key}:00`;
    } else if (reportType === "Month") {
      return `${key}.${("0" + month).slice(-2)}`;
    }
  };

  // Generate header row for a chunk of keys
  const generateHeader = (keyChunk) => {
    const deviceColumnTitle = "Device";
    const headerRow = [
      { label: deviceColumnTitle },
      ...keyChunk.map(key => ({ label: formatDate(key) }))
    ];
    return headerRow;
  };

  // Generate table rows for a specific chunk of keys
  const generateChunkedTableRows = (keyChunk, measurementsWithHeatmap) => {
    return measurementsWithHeatmap.map(measurement => {
      try {
        const deviceInfo = `${measurement.Label} (${measurement.HwId}) [${measurement.Unit}]`;
        const newRow = [
          // First element in the row is the device information
          { value: deviceInfo, percentage: "device" },
          // Then generate the row value objects for each key in this chunk
          ...keyChunk.map(key => {
            // Find the value and percentage for the current key from the measurement.Values array
            const valueObj = measurement.Values.find(val => val.date === key);
            return {
              value: valueObj ? valueObj.value.toFixed(1) : "0",
              percentage: valueObj ? valueObj.percentage : 0
            };
          })
        ];
        // Return new row (this adds it to tableRows)
        return newRow;
      } catch (error) {
        console.log("Failure in measurement data: ", error);
        return [];
      }
    });
  };



  // Get device cell styling
  const getDeviceCellSx = () => ({
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[200],
    borderRight: '2px solid',
    borderRightColor: theme.palette.primary.main,
    position: 'sticky',
    left: 0,
    zIndex: 1,
    // height: "60px",
    minWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: 1,
    color: theme.palette.text.primary,
    '@media print': {
      minWidth: '120px !important',
      maxWidth: '120px !important',
      height: 'auto !important',
      fontSize: '0.8rem !important',
      padding: '4px !important',
      wordWrap: 'break-word !important',
      whiteSpace: 'normal !important'
    }
  });

  // Get data cell styling based on value
  const getDataCellSx = (value) => {
    const getLightHeatmapColor = (percentage) => {
      if (percentage < 0.2) {
        return theme.palette.heatmap.level1;
      } else if (percentage < 0.4) {
        return theme.palette.heatmap.level2;
      } else if (percentage < 0.6) {
        return theme.palette.heatmap.level3;
      } else if (percentage < 0.8) {
        return theme.palette.heatmap.level4;
      } else {
        return theme.palette.heatmap.level5;
      }
    };

    return {
      textAlign: 'center',
      padding: 1,
      backgroundColor: heatmapOn ? getLightHeatmapColor(value.percentage) : 'transparent',
      fontWeight: value.percentage > 0.8 ? 'bold' : 'normal',
      border: '1px solid',
      borderColor: theme.palette.divider,
      color: theme.palette.text.primary,
      '@media print': {
        backgroundColor: heatmapOn ? getLightHeatmapColor(value.percentage) : 'transparent !important',
        // borderColor: '#cccccc !important',
        fontSize: '0.75rem !important',
        padding: '3px !important'
      }
    };
  };

  return (
    <Box>
      {keyChunks.map((keyChunk, chunkIndex) => {
        const headerRow = generateHeader(keyChunk);
        const dataRows = generateChunkedTableRows(keyChunk, measurementsWithHeatmap);

        // Generate chunk title based on the time range
        const getChunkTitle = (keyChunk, chunkIndex) => {
          if (keyChunk.length === 0) return '';

          const startKey = keyChunk[0];
          const endKey = keyChunk[keyChunk.length - 1];

          if (reportType === "Day") {
            // Convert to proper time format and determine AM/PM periods
            const startHour = parseInt(startKey);
            const endHour = parseInt(endKey);

            if (startHour < 12) {
              return `Morning Hours (${startKey}:00 - ${endKey}:59)`;
            } else {
              return `Evening Hours (${startKey}:00 - ${endKey}:59)`;
            }
          } else {
            // For days, show the range more clearly
            const chunkTitles = [
              `Days 1-10 (${startKey}.${("0" + month).slice(-2)} - ${endKey}.${("0" + month).slice(-2)})`,
              `Days 11-20 (${startKey}.${("0" + month).slice(-2)} - ${endKey}.${("0" + month).slice(-2)})`,
              `Days 21+ (${startKey}.${("0" + month).slice(-2)} - ${endKey}.${("0" + month).slice(-2)})`
            ];

            return chunkTitles[chunkIndex] || `Days ${startKey}.${("0" + month).slice(-2)} - ${endKey}.${("0" + month).slice(-2)}`;
          }
        };

        return (
          <Box key={`chunk-${chunkIndex}`} sx={{ mb: 3 }}>
            {/* {keyChunks.length > 1 && (
              <Typography
                variant="h6"
                sx={{
                  mb: 1,
                  textAlign: 'center',
                  color: theme.palette.primary.main,
                  fontWeight: 'bold'
                }}
              >
                {getChunkTitle(keyChunk, chunkIndex)}
              </Typography>
            )} */}

            <TableContainer
              component={Paper}
              sx={{
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: 2,
                '@media print': {
                  pageBreakInside: 'avoid',
                  marginBottom: '20px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.surface.printBorder}`
                }
              }}
            >
              <Table size="small">
                {/* HEADER ROW */}
                <TableHead>
                  <TableRow>
                    {headerRow.map((headerValue, index) => (
                      <TableCell
                        key={`header-${chunkIndex}-${index}`}
                        sx={{
                          backgroundColor: theme.palette.grey[100],
                          color: theme.palette.text.primary,
                          borderRight: '1px solid',
                          borderColor: theme.palette.divider,
                          textAlign: 'center',
                          px: 0.5,
                          py: 1,
                          fontWeight: 'bold',
                          '@media print': {
                            backgroundColor: `${theme.palette.surface.printBg} !important`,
                            color: 'black !important',
                            borderColor: `${theme.palette.surface.printBorder} !important`,
                            fontSize: '0.7rem !important',
                            fontWeight: 'bold !important'
                          }
                        }}
                      >
                        <Typography sx={{ fontWeight: 'bold', fontSize: 'inherit' }}>
                          {headerValue.label}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                {/* HEADER ROW END */}

                <TableBody>
                  {dataRows.map((dataValuesForDevice, index) => (
                    <TableRow
                      key={`${chunkIndex}-${index}rowValue`}
                      sx={{
                        ...(!heatmapOn && {
                          '&:nth-of-type(odd)': {
                            backgroundColor: theme.palette.action.hover,
                          }
                        }),
                        '&:hover': {
                          backgroundColor: theme.palette.action.selected,
                        }
                      }}
                    >
                      {dataValuesForDevice.map((value, i) => (
                        <TableCell
                          key={`${chunkIndex}-${i}colValue`}
                          sx={
                            value.percentage === "device"
                              ? getDeviceCellSx()
                              : getDataCellSx(value)
                          }
                        >
                          {value.percentage === "device" ? (
                            <Box sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start'
                            }}>
                              <Box component="span" sx={{ fontSize: '0.85em', color: 'inherit' }}>
                                {value.value}
                              </Box>
                            </Box>
                          ) : (
                            value.value
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
}

export default EnergiesTableContentPDF;